// Campos de formulário (DESIGN §5.2): rótulo acima, ajuda/erro abaixo, 52 de altura, 16 px (sem zoom no iOS).
// `dentroPainel`: fundo recuo sem borda (a borda aparece no foco).
import {
  forwardRef,
  useId,
  useRef,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { cn } from "../lib/cn";
import { formatarKg } from "../lib/numeros";
import { Icones } from "./icones";

const CLASSE_CAMPO =
  "h-controle w-full rounded-controle border px-4 text-corpo text-tinta placeholder:text-texto-3 " +
  "focus:outline-none focus:border-foco focus:ring-1 focus:ring-foco " +
  "disabled:bg-recuo disabled:text-texto-3 disabled:border-fio";

export function classesCampo(opcoes: { erro?: boolean; dentroPainel?: boolean } = {}): string {
  return cn(
    CLASSE_CAMPO,
    opcoes.dentroPainel ? "bg-recuo border-transparent" : "bg-painel border-fio-controle",
    // Com erro, o foco não troca a borda vermelha pela azul (o campo barrado recebe o foco de volta; folha
    // envio-apple-estados §3a): o anel de foco continua visível, só na cor do erro.
    opcoes.erro && "border-erro ring-1 ring-erro focus:border-erro focus:ring-erro",
  );
}

interface PropsMoldura {
  id: string;
  rotulo?: ReactNode;
  ajuda?: ReactNode;
  erro?: ReactNode;
  className?: string;
  children: ReactNode;
  /** Conteúdo à direita do rótulo (ex.: contador "32/280"). */
  extra?: ReactNode;
}

/** Moldura rótulo + controle + ajuda/erro. Use para montar controles próprios. */
export function MolduraCampo({ id, rotulo, ajuda, erro, className, children, extra }: PropsMoldura) {
  return (
    <div className={cn("flex flex-col", className)}>
      {(rotulo || extra) && (
        <div className="mb-2 flex items-baseline justify-between gap-3">
          {rotulo && (
            // entrelinha normal (15 px), como o `.lab` das pranchas do painel: 5 px a menos por campo que o text-apoio
            <label htmlFor={id} className="text-apoio leading-[normal] font-medium text-tinta">
              {rotulo}
            </label>
          )}
          {extra}
        </div>
      )}
      {children}
      {erro ? (
        <p id={`${id}-erro`} className="mt-1.5 flex items-start gap-1.5 text-rotulo text-erro">
          <Icones.erro size={16} aria-hidden className="mt-px shrink-0" />
          <span>{erro}</span>
        </p>
      ) : ajuda ? (
        <p id={`${id}-ajuda`} className="mt-1.5 text-rotulo text-texto-3">
          {ajuda}
        </p>
      ) : null}
    </div>
  );
}

function descricaoDe(id: string, erro?: ReactNode, ajuda?: ReactNode): string | undefined {
  if (erro) return `${id}-erro`;
  if (ajuda) return `${id}-ajuda`;
  return undefined;
}

export interface PropsCampo extends Omit<InputHTMLAttributes<HTMLInputElement>, "size"> {
  rotulo?: ReactNode;
  ajuda?: ReactNode;
  erro?: ReactNode;
  dentroPainel?: boolean;
  /** Estilo de código de acesso (maiúsculas, espaçado, placar). */
  codigo?: boolean;
  /** Ícone 20 em texto-3 dentro do campo, à esquerda (busca de colega; folha comunidade-fotos-marcacao §5). */
  iconeInicio?: typeof Icones.buscar;
  classNameMoldura?: string;
}

export const Campo = forwardRef<HTMLInputElement, PropsCampo>(function Campo(
  {
    rotulo,
    ajuda,
    erro,
    dentroPainel,
    codigo,
    iconeInicio: IconeInicio,
    className,
    classNameMoldura,
    id,
    type = "text",
    ...resto
  },
  ref,
) {
  const autoId = useId();
  const idCampo = id ?? autoId;
  const [visivel, setVisivel] = useState(false);
  const ehSenha = type === "password";

  const extrasCodigo = codigo
    ? {
        autoCapitalize: "characters",
        autoComplete: "one-time-code",
        autoCorrect: "off",
        spellCheck: false,
        inputMode: "text" as const,
        maxLength: 10,
      }
    : {};

  return (
    <MolduraCampo id={idCampo} rotulo={rotulo} ajuda={ajuda} erro={erro} className={classNameMoldura}>
      <div className="relative">
        {IconeInicio && (
          <IconeInicio
            size={20}
            aria-hidden
            className="pointer-events-none absolute top-1/2 left-4 -translate-y-1/2 text-texto-3"
          />
        )}
        <input
          ref={ref}
          id={idCampo}
          type={ehSenha && visivel ? "text" : type}
          aria-invalid={erro ? true : undefined}
          aria-describedby={descricaoDe(idCampo, erro, ajuda)}
          className={cn(
            classesCampo({ erro: !!erro, dentroPainel }),
            ehSenha && "pr-14",
            IconeInicio && "pl-11",
            codigo && "placar text-[18px] font-semibold uppercase tracking-[0.18em]",
            className,
          )}
          {...extrasCodigo}
          {...resto}
        />
        {ehSenha && (
          <button
            type="button"
            onClick={() => setVisivel((v) => !v)}
            aria-label={visivel ? "Ocultar senha" : "Mostrar senha"}
            aria-pressed={visivel}
            className="absolute inset-y-0 right-0 grid w-toque place-items-center rounded-r-controle text-texto-3 hover:text-tinta"
          >
            {visivel ? <Icones.ocultar size={20} aria-hidden /> : <Icones.mostrar size={20} aria-hidden />}
          </button>
        )}
      </div>
    </MolduraCampo>
  );
});

/** Só os dígitos, no máximo `n` ("482 913" e "482-913" colados viram "482913"). */
export const soDigitos = (texto: string, n = 6) => texto.replace(/\D/g, "").slice(0, n);

export interface PropsCampoDigitos {
  valor: string;
  aoMudar: (valor: string) => void;
  /** Chamado quando os 6 dígitos ficam completos (confere sozinho). */
  aoCompletar?: (valor: string) => void;
  /** `erro`: contorno de erro nas casas. `desligado`: código vencido ou cancelado (casas apagadas). */
  estado?: "erro" | "desligado";
  /** id do texto de ajuda ou de erro abaixo (aria-describedby). */
  idDescricao?: string;
  autoFocus?: boolean;
  className?: string;
}

/**
 * Campo de código de 6 números (DESIGN §5.2, variante "dígitos"; docs/prototipo/senha-propria-aluno.html §4 e §12): UM
 * input de verdade, transparente, sobre 6 casas de 60 de altura (44 de largura até 374 px, 48 a partir de 375), 8 px
 * entre as casas e um traço de 14 px depois da 3ª. Assim o colar, o preenchimento automático do sistema
 * (`one-time-code`: "Do Mail" no iPhone, Gboard no Android) e o leitor de tela funcionam sem truque.
 */
export const CampoDigitos = forwardRef<HTMLInputElement, PropsCampoDigitos>(function CampoDigitos(
  { valor, aoMudar, aoCompletar, estado, idDescricao, autoFocus, className },
  ref,
) {
  const [foco, setFoco] = useState(false);
  const mudar = (bruto: string) => {
    const v = soDigitos(bruto);
    aoMudar(v);
    if (v.length === 6 && v !== valor) aoCompletar?.(v);
  };
  // Casa ativa = a próxima a preencher (com os 6 preenchidos, nenhuma), só com foco e sem erro.
  const ativa = foco && estado === undefined ? valor.length : -1;
  const casas = [];
  for (let i = 0; i < 6; i++) {
    if (i === 3)
      casas.push(
        <span key="traco" aria-hidden className="grid w-3.5 shrink-0 place-items-center">
          <span className="h-0.5 w-2 rounded-xs bg-fio-controle" />
        </span>,
      );
    casas.push(
      <span
        key={i}
        aria-hidden
        className={cn(
          "relative grid h-15 w-11 shrink-0 place-items-center rounded-controle text-[28px] leading-none font-bold text-tinta placar min-[375px]:w-12",
          estado === "desligado"
            ? "bg-recuo text-texto-3 ring-1 ring-fio ring-inset"
            : estado === "erro"
              ? "bg-painel ring-2 ring-erro ring-inset"
              : i === ativa
                ? "bg-painel ring-2 ring-foco ring-inset"
                : "bg-painel ring-1 ring-fio-controle ring-inset",
        )}
      >
        {valor[i] ?? ""}
        {i === ativa && <span className="absolute h-7 w-0.5 bg-foco" />}
      </span>,
    );
    if (i !== 2 && i !== 5) casas.push(<span key={`e${i}`} aria-hidden className="w-2 shrink-0" />);
  }
  return (
    <div className={cn("relative flex w-max items-center", className)}>
      {casas}
      <input
        ref={ref}
        type="text"
        inputMode="numeric"
        pattern="\d*"
        autoComplete="one-time-code"
        autoCorrect="off"
        spellCheck={false}
        maxLength={6}
        aria-label="Código de verificação, 6 dígitos"
        aria-describedby={idDescricao}
        aria-invalid={estado === "erro" || undefined}
        disabled={estado === "desligado"}
        autoFocus={autoFocus}
        value={valor}
        onChange={(e) => mudar(e.target.value)}
        onPaste={(e) => {
          e.preventDefault();
          mudar(e.clipboardData.getData("text"));
        }}
        onFocus={() => setFoco(true)}
        onBlur={() => setFoco(false)}
        // Texto e cursor transparentes (as casas mostram os dígitos); o input continua "visível" para o sistema
        // oferecer o código do Mail/SMS.
        className="absolute inset-0 h-full w-full cursor-text rounded-controle bg-transparent text-transparent caret-transparent outline-none selection:bg-transparent selection:text-transparent focus-visible:outline-none"
      />
    </div>
  );
});

export interface PropsAreaTexto extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  rotulo?: ReactNode;
  ajuda?: ReactNode;
  erro?: ReactNode;
  dentroPainel?: boolean;
  /** Mostra contador "n/max" quando há maxLength. */
  contador?: boolean;
}

export const AreaTexto = forwardRef<HTMLTextAreaElement, PropsAreaTexto>(function AreaTexto(
  { rotulo, ajuda, erro, dentroPainel, contador = true, className, id, maxLength, value, ...resto },
  ref,
) {
  const autoId = useId();
  const idCampo = id ?? autoId;
  const tamanho = typeof value === "string" ? value.length : 0;
  return (
    <MolduraCampo
      id={idCampo}
      rotulo={rotulo}
      ajuda={ajuda}
      erro={erro}
      extra={
        contador && maxLength ? (
          <span className="text-rotulo text-texto-3 placar" aria-live="polite">
            {tamanho}/{maxLength}
          </span>
        ) : undefined
      }
    >
      <textarea
        ref={ref}
        id={idCampo}
        maxLength={maxLength}
        value={value}
        aria-invalid={erro ? true : undefined}
        aria-describedby={descricaoDe(idCampo, erro, ajuda)}
        className={cn(classesCampo({ erro: !!erro, dentroPainel }), "h-auto min-h-28 py-3", className)}
        {...resto}
      />
    </MolduraCampo>
  );
});

export interface PropsStepper {
  rotulo: string;
  valor: number;
  aoMudar: (valor: number) => void;
  passo?: number;
  min?: number;
  max?: number;
  unidade?: "kg" | "reps" | string;
  /** Casas decimais exibidas (carga: 1). */
  casas?: number;
  className?: string;
}

/** Stepper numérico 56 de altura (carga passo 2,5; repetições passo 1). Toque longo repete. */
export function Stepper({
  rotulo,
  valor,
  aoMudar,
  passo = 1,
  min = 0,
  max = 999,
  unidade,
  casas = 0,
  className,
}: PropsStepper) {
  const id = useId();
  const repetidor = useRef<number | null>(null);
  const valorRef = useRef(valor);
  valorRef.current = valor;

  const ajustar = (delta: number) => {
    const bruto = valorRef.current + delta;
    const arredondado = Math.round(bruto * 10 ** casas) / 10 ** casas;
    const novo = Math.min(max, Math.max(min, arredondado));
    valorRef.current = novo;
    aoMudar(novo);
  };
  const iniciarRepeticao = (delta: number) => {
    ajustar(delta);
    const t0 = window.setTimeout(() => {
      repetidor.current = window.setInterval(() => ajustar(delta), 90);
    }, 400);
    repetidor.current = t0;
  };
  const pararRepeticao = () => {
    if (repetidor.current) {
      window.clearTimeout(repetidor.current);
      window.clearInterval(repetidor.current);
    }
    repetidor.current = null;
  };

  const botao = (delta: number, rotuloBotao: string, Icone: typeof Icones.mais1) => (
    <button
      type="button"
      aria-label={rotuloBotao}
      className="grid size-12 shrink-0 place-items-center rounded-controle bg-recuo text-tinta hover:bg-fio disabled:opacity-40"
      disabled={delta < 0 ? valor <= min : valor >= max}
      onPointerDown={() => iniciarRepeticao(delta)}
      onPointerUp={pararRepeticao}
      onPointerLeave={pararRepeticao}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          ajustar(delta);
        }
      }}
    >
      <Icone size={20} aria-hidden />
    </button>
  );

  return (
    <div className={cn("flex flex-col items-stretch", className)}>
      <label htmlFor={id} className="sr-only">
        {rotulo}
      </label>
      <div className="flex h-14 items-center gap-2">
        {botao(-passo, `Diminuir ${rotulo}`, Icones.menos1)}
        <input
          id={id}
          type="number"
          inputMode="decimal"
          step={passo}
          min={min}
          max={max}
          value={casas > 0 ? valor.toFixed(casas) : valor}
          onChange={(e) => {
            const n = Number(e.target.value.replace(",", "."));
            if (Number.isFinite(n)) aoMudar(Math.min(max, Math.max(min, n)));
          }}
          className="h-14 w-full min-w-0 rounded-controle bg-transparent text-center text-numero placar focus:outline-none focus:ring-1 focus:ring-foco [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        />
        {botao(passo, `Aumentar ${rotulo}`, Icones.mais1)}
      </div>
      {unidade && (
        <span className="text-center text-rotulo text-texto-3" aria-hidden>
          {unidade === "kg" && casas > 0 ? `${formatarKg(valor, false)} kg` : unidade}
        </span>
      )}
    </div>
  );
}
